import { useMemo, useState } from "react";
import { useHistory } from "react-router";
import { toast } from "react-toastify";
import { FiTrash2 } from "react-icons/fi";
import { PathName } from "../../../constants/PathName";
import { Constants } from "../../../constants/Constants";
import {
	useDeleteFormBuilderFormMutation,
	useListFormBuilderFormsQuery,
} from "../../../generated/nuggetKmsApi/graphql";
import { useExportFormResponses } from "./useExportFormResponses";
import { BsFiletypeCsv } from "react-icons/bs";

const PAGE_SIZE = 10;

interface FormsTableRowProps {
	row: { id: number; code: string; name: string; updatedAt: Date; submissionCount: number };
	deleting: boolean;
	onRowClick: (id: number) => void;
	onDelete: (event: React.MouseEvent<HTMLButtonElement>, id: number) => void;
}

const FormsTableRow = ({ row, deleting, onRowClick, onDelete }: FormsTableRowProps) => {
	const { exportResponses, loading: exportLoading } = useExportFormResponses(row.id);

	const handleExport = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		exportResponses();
	};

	return (
		<tr onClick={() => onRowClick(row.id)}>
			<td>{row.id}</td>
			<td>{row.code}</td>
			<td dangerouslySetInnerHTML={{ __html: row.name }} />
			<td>{new Date(row.updatedAt).toLocaleString()}</td>
			<td>{row.submissionCount}</td>
			<td className="table-actions">
				<button
					type="button"
					className="formBuilder-button formBuilder-button--secondary"
					onClick={handleExport}
					disabled={exportLoading}
					aria-label="Export responses"
				>
					<span>Export CSV</span>
					<BsFiletypeCsv />
				</button>
				<button
					type="button"
					className="formBuilder-button formBuilder-button--danger"
					onClick={(event) => onDelete(event, row.id)}
					disabled={deleting}
					aria-label="Delete form"
				>
					<FiTrash2 />
				</button>
			</td>
		</tr>
	);
};

export const FormsTable = () => {
	const history = useHistory();
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);

	const { data, loading, refetch } = useListFormBuilderFormsQuery({
		variables: {
			input: {
				search: search.trim() ? search.trim() : null,
				pagination: { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
			},
		},
		context: { clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME },
		fetchPolicy: "network-only",
	});

	const [deleteForm, { loading: deleting }] = useDeleteFormBuilderFormMutation({
		context: { clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME },
		onCompleted: () => {
			toast.success("Form deleted.");
			refetch();
		},
		onError: (error) => {
			toast.error(error.message || "Failed to delete form.");
		},
	});

	const rows = data?.listFormBuilderForms.rows ?? [];
	const totalCount = data?.listFormBuilderForms.count ?? 0;
	const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)), [totalCount]);

	const handleRowClick = (id: number) => {
		history.push(PathName.FORM_BUILDER_FORMS_EDIT(String(id)));
	};

	const handleDelete = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
		event.stopPropagation();
		if (!window.confirm("Delete this form? This action cannot be undone.")) {
			return;
		}
		deleteForm({ variables: { id } });
	};

	const handleSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	return (
		<div className="formBuilder-table">
			<div className="formBuilder-tableHeader row">
				<div className="col-xs-12 col-sm-6">
					<input
						type="text"
						placeholder="Filter by code or name"
						value={search}
						onChange={(event) => handleSearchChange(event.target.value)}
					/>
				</div>
				<div className="col-xs-12 col-sm-6 text-right">
					<button
						type="button"
						className="formBuilder-button formBuilder-button--primary"
						onClick={() => history.push(PathName.FORM_BUILDER_FORMS_ADD)}
					>
						+ Add Form
					</button>
				</div>
			</div>
			<table>
				<thead>
					<tr>
						<th>ID</th>
						<th>Code</th>
						<th>Name</th>
						<th>Updated</th>
						<th>Submissions</th>
						<th />
					</tr>
				</thead>
				<tbody>
					{loading ? (
						<tr>
							<td colSpan={5}>Loading...</td>
						</tr>
					) : rows.length === 0 ? (
						<tr>
							<td colSpan={5}>No forms found.</td>
						</tr>
					) : (
						rows.map((row) => (
							<FormsTableRow
								key={row.id}
								row={row}
								deleting={deleting}
								onRowClick={handleRowClick}
								onDelete={handleDelete}
							/>
						))
					)}
				</tbody>
			</table>
			<div className="formBuilder-pagination">
				<button
					type="button"
					onClick={() => setPage((prev) => Math.max(1, prev - 1))}
					disabled={page === 1}
				>
					Prev
				</button>
				<span>
					Page {page} of {totalPages}
				</span>
				<button
					type="button"
					onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
					disabled={page >= totalPages}
				>
					Next
				</button>
			</div>
		</div>
	);
};
